// Authenticated API client with automatic token refresh on 401
// Feature: 021-auth-infra-improvements
// Updated: 025-persistent-auth - Added queue pattern and multi-tab sync

// Determine API URL - use relative path in development to go through Vite proxy
function getApiUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;

  // If no env URL, use relative path (goes through Vite proxy)
  if (!envUrl) {
    return '/api';
  }

  // If env URL contains localhost but we're not on localhost,
  // we're likely in Docker - use relative path for proxy
  if (envUrl.includes('localhost') && !window.location.host.includes('localhost')) {
    return '/api';
  }

  return envUrl;
}

export const API_URL = getApiUrl();

export interface ApiError {
  error: string;
  message: string;
  details?: Array<{ field: string; message: string }>;
}

// ============================================================================
// Request Queue Pattern (T004)
// Queued requests wait for refresh to complete, then resolve/reject together
// ============================================================================

interface QueuedRequest {
  resolve: (success: boolean) => void;
  reject: (error: Error) => void;
}

// Flag to prevent multiple concurrent refresh attempts
let isRefreshing = false;

// Queue of requests waiting for token refresh to complete
let failedQueue: QueuedRequest[] = [];

// Track last successful refresh to debounce rapid refresh attempts
let lastSuccessfulRefresh = 0;
const REFRESH_DEBOUNCE_MS = 5000; // 5 seconds - reduced from 10s for better UX

// ============================================================================
// localStorage-based Cross-Tab Synchronization (more reliable than BroadcastChannel)
// localStorage is synchronous and immediately visible to all tabs
// ============================================================================

const STORAGE_KEY_LAST_REFRESH = 'rlview_auth_last_refresh';

// Initialize from localStorage on load (sync with other tabs immediately)
function initializeFromStorage(): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_LAST_REFRESH);
    if (stored) {
      const timestamp = parseInt(stored, 10);
      if (!isNaN(timestamp) && timestamp > lastSuccessfulRefresh) {
        lastSuccessfulRefresh = timestamp;
        console.log(`[Auth] Initialized lastSuccessfulRefresh from storage: ${timestamp}`);
      }
    }
  } catch {
    // localStorage might not be available (private browsing, etc.)
  }
}

// Update localStorage when refresh succeeds
function updateStorageTimestamp(timestamp: number): void {
  try {
    localStorage.setItem(STORAGE_KEY_LAST_REFRESH, timestamp.toString());
  } catch {
    // Ignore storage errors
  }
}

// Get the latest refresh timestamp from localStorage (cross-tab sync)
function getLatestRefreshTimestamp(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_LAST_REFRESH);
    if (stored) {
      const timestamp = parseInt(stored, 10);
      if (!isNaN(timestamp)) {
        // Update local variable if storage has newer timestamp
        if (timestamp > lastSuccessfulRefresh) {
          lastSuccessfulRefresh = timestamp;
        }
        return Math.max(timestamp, lastSuccessfulRefresh);
      }
    }
  } catch {
    // localStorage might not be available
  }
  return lastSuccessfulRefresh;
}

// Listen for storage changes from other tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY_LAST_REFRESH && event.newValue) {
      const timestamp = parseInt(event.newValue, 10);
      if (!isNaN(timestamp) && timestamp > lastSuccessfulRefresh) {
        lastSuccessfulRefresh = timestamp;
        console.log(`[Auth] Storage event: another tab refreshed at ${timestamp}`);
      }
    }
  });

  // Initialize on load
  initializeFromStorage();
}

// T024: Network retry configuration (exponential backoff)
const MAX_NETWORK_RETRIES = 2;
let networkRetryCount = 0;

// T026: Error type enumeration for debugging
export enum AuthErrorType {
  SUCCESS = 'SUCCESS',
  NETWORK_ERROR = 'NETWORK_ERROR',
  AUTH_EXPIRED = 'AUTH_EXPIRED',  // 401 - session definitively expired
  SERVER_ERROR = 'SERVER_ERROR',   // 5xx - temporary server issue
  CLIENT_ERROR = 'CLIENT_ERROR',   // 4xx (not 401) - bad request
}

// Session expired state - when true, stop all API calls and polling
let isSessionExpired = false;

// Callbacks to notify when session expires
type SessionExpiredCallback = () => void;
const sessionExpiredCallbacks: Set<SessionExpiredCallback> = new Set();

// ============================================================================
// Process Queue (T005)
// Resolves or rejects all queued requests after refresh completes
// ============================================================================

function processQueue(error: Error | null, success: boolean): void {
  failedQueue.forEach(request => {
    if (error) {
      request.reject(error);
    } else {
      request.resolve(success);
    }
  });
  failedQueue = [];
}

// ============================================================================
// BroadcastChannel for Multi-Tab Sync (T007, T008, T009)
// Prevents race conditions when multiple tabs refresh simultaneously
// ============================================================================

type AuthEventType = 'TOKEN_REFRESHED' | 'SESSION_EXPIRED';

interface AuthBroadcastMessage {
  type: AuthEventType;
  timestamp: number;
}

// BroadcastChannel instance - null if not supported (older Safari)
let authChannel: BroadcastChannel | null = null;

// Initialize BroadcastChannel if supported
if (typeof BroadcastChannel !== 'undefined') {
  try {
    authChannel = new BroadcastChannel('rlview_auth');

    // Listen for events from other tabs (T009)
    // IMPORTANT: Only sync TOKEN_REFRESHED, not SESSION_EXPIRED
    // Each tab should determine session state independently to avoid race conditions
    authChannel.onmessage = (event: MessageEvent<AuthBroadcastMessage>) => {
      const { type, timestamp } = event.data;

      switch (type) {
        case 'TOKEN_REFRESHED':
          // Another tab refreshed successfully - update our timestamp
          // This prevents us from doing a redundant refresh
          if (timestamp > lastSuccessfulRefresh) {
            lastSuccessfulRefresh = timestamp;
            console.log('[Auth] Token refreshed by another tab');
          }
          break;

        case 'SESSION_EXPIRED':
          // DISABLED: Don't auto-expire on other tab's message
          // Race condition: if Tab A refreshes and Tab B uses old token,
          // Tab B would incorrectly signal SESSION_EXPIRED
          // Each tab should independently determine if session is expired
          console.log('[Auth] Ignoring SESSION_EXPIRED from another tab - will verify independently');
          break;
      }
    };
  } catch {
    console.warn('[Auth] BroadcastChannel not available');
  }
}

// Notify other tabs about auth events (T008)
function notifyOtherTabs(type: AuthEventType): void {
  if (authChannel) {
    try {
      authChannel.postMessage({ type, timestamp: Date.now() } as AuthBroadcastMessage);
    } catch {
      // Ignore broadcast errors (channel might be closed)
    }
  }
}

/**
 * Subscribe to session expired events.
 * Use this to react when the user's session definitively expires (e.g., redirect to login).
 *
 * @param callback - Function to call when session expires
 * @returns Unsubscribe function to remove the listener
 * @example
 * const unsubscribe = onSessionExpired(() => {
 *   setUser(null);
 *   navigate('/login');
 * });
 * // Later: unsubscribe();
 */
export function onSessionExpired(callback: SessionExpiredCallback): () => void {
  sessionExpiredCallbacks.add(callback);
  return () => sessionExpiredCallbacks.delete(callback);
}

/**
 * Check if the session is still valid.
 * Use this in polling intervals to stop unnecessary API calls when session is expired.
 *
 * @returns true if session is valid, false if expired
 */
export function isSessionValid(): boolean {
  return !isSessionExpired;
}

/**
 * Reset the session state after a successful login.
 * This clears the expired flag and updates the last refresh timestamp.
 * Call this after user successfully logs in or registers.
 */
export function resetSessionState(): void {
  isSessionExpired = false;
  const now = Date.now();
  lastSuccessfulRefresh = now; // Consider login as a successful "refresh"
  updateStorageTimestamp(now); // Sync to other tabs via localStorage
}

// Notify all callbacks that session expired
function notifySessionExpired(): void {
  if (isSessionExpired) return; // Already notified
  isSessionExpired = true;

  // Notify other tabs first
  notifyOtherTabs('SESSION_EXPIRED');

  // Then notify local callbacks
  sessionExpiredCallbacks.forEach(callback => {
    try {
      callback();
    } catch {
      // Ignore callback errors
    }
  });
}

/**
 * Attempt to refresh the access token using the refresh token cookie.
 *
 * This function implements several safeguards:
 * - **Debouncing**: Skips refresh if one succeeded recently (within 5 seconds)
 * - **Queue pattern**: Concurrent 401s queue up and share a single refresh
 * - **Multi-tab sync**: Uses BroadcastChannel to coordinate across tabs
 * - **Error resilience**: Only marks session expired for 401, not for network/server errors
 *
 * @param forceBypassDebounce - If true, bypasses the debounce check (use for visibility change after long inactivity)
 * @returns Promise resolving to true if refresh succeeded, false otherwise
 *
 * @example
 * // Normal usage - respects debounce
 * const success = await attemptTokenRefresh();
 *
 * // After long inactivity - force refresh
 * const success = await attemptTokenRefresh(true);
 */
export async function attemptTokenRefresh(forceBypassDebounce = false): Promise<boolean> {
  // If session is already marked as expired, don't try to refresh
  if (isSessionExpired) {
    console.log(`[Auth] (${AuthErrorType.AUTH_EXPIRED}) Session already expired - skipping refresh`);
    return false;
  }

  // Debounce: if we refreshed successfully recently, skip this refresh
  // This prevents race conditions where multiple refresh calls use the same token
  // before the new cookie is stored by the browser
  // Use getLatestRefreshTimestamp() to check localStorage for cross-tab sync
  const now = Date.now();
  const latestRefresh = getLatestRefreshTimestamp();
  if (!forceBypassDebounce && now - latestRefresh < REFRESH_DEBOUNCE_MS) {
    console.log(`[Auth] (${AuthErrorType.SUCCESS}) Skipping refresh - last successful refresh was ${now - latestRefresh}ms ago (from ${latestRefresh === lastSuccessfulRefresh ? 'local' : 'storage'})`);
    return true; // Assume the recent refresh is still valid
  }

  // If already refreshing, queue this request instead of starting a new refresh
  if (isRefreshing) {
    console.log('[Auth] Refresh in progress - queueing request');
    return new Promise<boolean>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    // Note: No Content-Type header and no body - the refresh token is in HttpOnly cookies
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (response.ok) {
      const refreshTime = Date.now();
      lastSuccessfulRefresh = refreshTime;
      updateStorageTimestamp(refreshTime); // Cross-tab sync via localStorage
      networkRetryCount = 0; // T024: Reset retry count on success
      console.log(`[Auth] (${AuthErrorType.SUCCESS}) Token refresh successful at ${refreshTime}`);

      // Notify other tabs about successful refresh
      notifyOtherTabs('TOKEN_REFRESHED');

      // Process queued requests with success
      processQueue(null, true);
      return true;
    }

    // T025/T026: Clear error type distinction based on HTTP status
    if (response.status === 401) {
      // Check if another tab recently refreshed successfully (race condition protection)
      // If yes, our refresh failed because we used an old token - but session is still valid
      // Use getLatestRefreshTimestamp() to check localStorage for most up-to-date info
      const latestRefreshTime = getLatestRefreshTimestamp();
      const timeSinceLastRefresh = Date.now() - latestRefreshTime;
      if (timeSinceLastRefresh < REFRESH_DEBOUNCE_MS * 2) {
        // Another tab likely refreshed - don't expire, let caller retry
        console.log(`[Auth] (${AuthErrorType.SUCCESS}) Refresh failed (401) but another tab refreshed ${timeSinceLastRefresh}ms ago - session still valid, caller should retry`);
        processQueue(null, true); // Signal success so callers retry with new token
        return true;
      }

      // No recent refresh - session is truly expired
      console.log(`[Auth] (${AuthErrorType.AUTH_EXPIRED}) Refresh token invalid or expired (no recent refresh in ${timeSinceLastRefresh}ms) - marking session as expired`);
      notifySessionExpired();
      processQueue(new Error('Session expired'), false);
    } else if (response.status >= 500) {
      // T025: 5xx errors are temporary server issues
      console.warn(`[Auth] (${AuthErrorType.SERVER_ERROR}) Token refresh failed with status ${response.status} - not marking session as expired`);
      processQueue(null, false);
    } else {
      // T026: 4xx (not 401) are client errors
      console.warn(`[Auth] (${AuthErrorType.CLIENT_ERROR}) Token refresh failed with status ${response.status} - not marking session as expired`);
      processQueue(null, false);
    }
    return false;
  } catch (error) {
    // T023: Network error - don't mark session as expired, it's likely temporary
    // T024: Simple retry tracking (actual retry is handled by caller/proactive refresh)
    networkRetryCount++;
    console.warn(`[Auth] (${AuthErrorType.NETWORK_ERROR}) Token refresh failed due to network error (attempt ${networkRetryCount}/${MAX_NETWORK_RETRIES}):`, error);

    // Don't reject queued requests on network error - they can retry later
    processQueue(null, false);
    return false;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Authenticated fetch wrapper with automatic 401 retry.
 *
 * This is the primary function for making authenticated API calls.
 * On 401 response, it automatically attempts to refresh the token and retries the request.
 *
 * @template T - The expected response type
 * @param endpoint - API endpoint path (will be prefixed with API_URL)
 * @param options - Standard fetch options (method, body, headers, etc.)
 * @param skipRetry - If true, skips the 401 retry logic (used to prevent infinite retry loops)
 * @returns Promise resolving to the parsed JSON response
 * @throws {ApiError} When the response is not OK
 *
 * @example
 * // GET request
 * const user = await authenticatedFetch<User>('/users/me');
 *
 * // POST request
 * const result = await authenticatedFetch<Response>('/api/data', {
 *   method: 'POST',
 *   body: JSON.stringify({ key: 'value' })
 * });
 */
export async function authenticatedFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  skipRetry = false
): Promise<T> {
  const makeRequest = async (): Promise<Response> => {
    // Only set Content-Type if there's a body to send
    const headers: HeadersInit = options.body
      ? { 'Content-Type': 'application/json', ...options.headers }
      : { ...options.headers };

    return fetch(`${API_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers,
    });
  };

  let response = await makeRequest();

  // If we get a 401 and haven't already tried to refresh, attempt token refresh
  if (response.status === 401 && !skipRetry) {
    const refreshed = await attemptTokenRefresh();

    if (refreshed) {
      // Retry the original request with the new token
      response = await makeRequest();
    }
  }

  // Parse response
  const data = await response.json();

  if (!response.ok) {
    throw data as ApiError;
  }

  return data as T;
}

/**
 * Non-authenticated fetch for public endpoints.
 *
 * Still sends cookies (credentials: 'include') so the backend can optionally
 * use auth info for features like checking if a user has liked something.
 * Does NOT automatically retry on 401.
 *
 * @template T - The expected response type
 * @param endpoint - API endpoint path (will be prefixed with API_URL)
 * @param options - Standard fetch options (method, body, headers, etc.)
 * @returns Promise resolving to the parsed JSON response
 * @throws {ApiError} When the response is not OK
 *
 * @example
 * // Public replay list
 * const replays = await publicFetch<{ replays: Replay[] }>('/api/replays');
 */
export async function publicFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Only set Content-Type if there's a body to send
  const headers: HeadersInit = options.body
    ? { 'Content-Type': 'application/json', ...options.headers }
    : { ...options.headers };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    credentials: 'include', // Send cookies for optional auth
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw data as ApiError;
  }

  return data as T;
}
